/**
 * Button Component
 *
 * Versatile button component with multiple variants and sizes.
 * Supports loading states, icons, and various visual styles.
 *
 * Usage:
 *   <Button variant="primary" size="md" onClick={handleClick}>
 *     Click me
 *   </Button>
 *   <Button variant="error" icon={<TrashIcon />} iconOnly />
 *   <Button variant="link">Learn more</Button>
 */

import React from 'react';
import { cn } from '@/lib/utils';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Visual variant of the button */
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'error' | 'link';
  /** Size of the button */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  /** Loading state - shows spinner */
  loading?: boolean;
  /** Optional icon to display before text */
  icon?: React.ReactNode;
  /** Optional icon to display after text */
  iconAfter?: React.ReactNode;
  /** Icon-only button (adjusts padding) */
  iconOnly?: boolean;
  /** Full width button */
  fullWidth?: boolean;
}

export const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  size = 'md',
  loading = false,
  icon,
  iconAfter,
  iconOnly = false,
  fullWidth = false,
  className = '',
  disabled,
  type = 'button',
  ...props
}) => {
  // Variant-specific styles — all resolve to the central design tokens
  // (src/index.css), so a palette change there updates every button.
  // One solid accent per surface (primary, or danger for a destructive confirm); everything
  // else is quiet — a hairline neutral outline or no chrome at all.
  const variantStyles = {
    // Theme board: orange for the one next action (navy text, never white), navy for
    // confirm-type actions, a line-ring ghost for everything else.
    primary: cn(
      'bg-brand text-brand-on',
      'hover:bg-brand-hover',
      'active:bg-brand-hover'
    ),
    secondary: cn(
      'bg-navy text-on-navy',
      'hover:bg-navy-3',
      'active:bg-navy-3'
    ),
    outline: cn(
      'bg-transparent text-ink shadow-ring-line',
      'hover:shadow-ring-ink',
      'active:bg-inset'
    ),
    ghost: cn(
      'bg-transparent text-ink-2',
      'hover:bg-inset hover:text-ink',
      'active:bg-inset'
    ),
    danger: cn(
      'bg-bad text-card',
      'hover:opacity-90',
      'active:opacity-80'
    ),
    error: cn(
      'bg-bad text-card',
      'hover:opacity-90',
      'active:opacity-80'
    ),
    link: cn(
      'text-brand-ink hover:underline bg-transparent p-0',
      'hover:text-brand-ink'
    ),
  };

  // Size-specific styles (different for icon-only buttons)
  const getSizeClasses = () => {
    if (iconOnly) {
      return {
        xs: 'p-1',
        sm: 'p-1.5',
        md: 'p-2',
        lg: 'p-2.5',
        xl: 'p-3'
      }[size];
    }

    // Fixed heights so buttons side by side line up whatever their content (icon, spinner).
    return {
      xs: 'h-8 px-3 text-xs gap-1',
      sm: 'h-9 px-3.5 text-[13px] gap-1.5',
      md: 'h-11 px-[18px] text-sm gap-2',
      lg: 'h-12 px-5 text-base gap-2',
      xl: 'h-14 px-6 text-base gap-2.5',
    }[size];
  };

  // Loading spinner component
  const Spinner = () => (
    <svg
      className="animate-spin h-4 w-4"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );

  return (
    <button
      type={type}
      className={cn(
        // Base styles
        'inline-flex items-center justify-center',
        'font-display font-bold rounded-btn whitespace-nowrap',
        'transition-colors duration-150',
        // Keyboard focus only — no ring left behind after a mouse click.
        'focus:outline-none focus-visible:ring-[3px] focus-visible:ring-brand focus-visible:ring-offset-2',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        // Variant styles
        variantStyles[variant],
        // Size styles
        getSizeClasses(),
        // Width
        fullWidth && 'w-full',
        // Custom classes
        className
      )}
      disabled={disabled || loading}
      {...props}
    >
      {loading && <Spinner />}
      {!loading && icon && <span className="flex-shrink-0">{icon}</span>}
      {!iconOnly && children}
      {!loading && iconAfter && <span className="flex-shrink-0">{iconAfter}</span>}
    </button>
  );
};

// Export default
export default Button;
